import { useEffect, useRef, useState, useCallback } from "react";
import { http } from "../lib/api";
import { Input } from "./ui/input";
import { MagnifyingGlass } from "@phosphor-icons/react";

/**
 * Live ticker autocomplete. Debounced 300ms.
 * onSelect receives: { symbol, name, exchange }
 */
export default function TickerSearch({ value, onChange, onSelect, placeholder, testId = "ticker-search" }) {
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const search = useCallback((text) => {
    clearTimeout(timerRef.current);
    if (!text || text.length < 2) { setResults([]); return; }
    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await http.get("/market/ticker-search", { params: { q: text } });
        setResults(data.results || []);
        setOpen(true);
      } catch (err) { console.error("ticker search failed", err); setResults([]); }
      finally { setLoading(false); }
    }, 300);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input
          data-testid={testId}
          value={q}
          onChange={(e) => {
            const v = e.target.value.toUpperCase();
            setQ(v); onChange?.(v); search(v);
          }}
          onFocus={() => q && results.length > 0 && setOpen(true)}
          placeholder={placeholder || "Search TCS, INFY, RELIANCE…"}
          className="pl-9 bg-zinc-900 border-zinc-800 uppercase"
          autoComplete="off"
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-zinc-950 border border-zinc-800 rounded-md shadow-xl max-h-64 overflow-y-auto"
             data-testid={`${testId}-results`}>
          {results.map((r, i) => (
            <button
              type="button"
              key={`${r.symbol}-${r.exchange}-${i}`}
              onClick={() => {
                setQ(r.symbol); onChange?.(r.symbol);
                onSelect?.(r);
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-zinc-900 border-b border-zinc-900 last:border-0 flex items-center justify-between gap-3"
              data-testid={`${testId}-result-${r.symbol}`}
            >
              <div className="min-w-0">
                <div className="font-medium text-sm">{r.symbol}
                  <span className="ml-2 text-[10px] font-mono uppercase text-zinc-500">{r.exchange}</span>
                </div>
                <div className="text-xs text-zinc-500 truncate">{r.name || r.exchange_disp}</div>
              </div>
              {r.type && <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-900 text-zinc-500">{r.type}</span>}
            </button>
          ))}
        </div>
      )}
      {loading && q.length >= 2 && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500 animate-pulse">…</div>
      )}
    </div>
  );
}
